import React, { useState, useEffect } from 'react';
import { X, Trash, FloppyDisk } from '@phosphor-icons/react';
import type {
  ConopsFeatureType,
  ConopsProperties,
  AmsZoneProperties,
  HandoverPointProperties,
  PushbackProfileProperties,
  TaxiRouteProperties,
  AircraftCat,
  Movement,
  RwyConfig,
} from '../../types/conops';

// ─── Aviation color presets ────────────────────────────────────────────────────
const AMS_ZONE_COLORS = [
  { label: 'Azul', value: '#4A90D9' },
  { label: 'Verde', value: '#27AE60' },
  { label: 'Naranja', value: '#F26430' },
  { label: 'Violeta', value: '#8E44AD' },
  { label: 'Cian', value: '#1ABC9C' },
  { label: 'Amarillo', value: '#F39C12' },
];

// ─── Small reusable sub-components ────────────────────────────────────────────

interface ToggleGroupProps<T extends string> {
  label: string;
  options: { label: string; value: T }[];
  value: T;
  onChange: (v: T) => void;
  activeColor?: string;
}

function ToggleGroup<T extends string>({
  label,
  options,
  value,
  onChange,
  activeColor = '#F26430',
}: ToggleGroupProps<T>) {
  return (
    <div className="mb-3">
      <label className="block text-xs font-semibold text-[var(--text-secondary)] mb-1 uppercase tracking-wide">
        {label}
      </label>
      <div className="flex gap-1">
        {options.map((opt) => (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            className={`flex-1 py-1.5 text-xs font-bold rounded border transition-all ${
              value === opt.value
                ? 'text-white border-transparent'
                : 'border-[var(--border)] text-[var(--text-secondary)] hover:border-[var(--text-secondary)]'
            }`}
            style={value === opt.value ? { backgroundColor: activeColor, borderColor: activeColor } : {}}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

interface FieldProps {
  label: string;
  children: React.ReactNode;
}

const Field: React.FC<FieldProps> = ({ label, children }) => (
  <div className="mb-3">
    <label className="block text-xs font-semibold text-[var(--text-secondary)] mb-1 uppercase tracking-wide">
      {label}
    </label>
    {children}
  </div>
);

const inputClass =
  'w-full px-2.5 py-1.5 rounded-md border border-[var(--border)] bg-[var(--surface-inset)] text-[var(--text-primary)] text-sm focus:outline-none focus:ring-1 focus:ring-[#F26430]';

// ─── Form sections by feature type ────────────────────────────────────────────

function AmsZoneForm({
  data,
  onChange,
}: {
  data: Partial<AmsZoneProperties>;
  onChange: (d: Partial<AmsZoneProperties>) => void;
}) {
  return (
    <>
      <Field label="ID de Zona">
        <input
          className={inputClass}
          value={data.id || ''}
          placeholder="APN-T2"
          onChange={(e) => onChange({ ...data, id: e.target.value })}
        />
      </Field>
      <Field label="Nombre">
        <input
          className={inputClass}
          value={data.name || ''}
          placeholder="Plataforma Terminal 2"
          onChange={(e) => onChange({ ...data, name: e.target.value })}
        />
      </Field>
      <Field label="Color">
        <div className="flex gap-2 flex-wrap">
          {AMS_ZONE_COLORS.map((c) => (
            <button
              key={c.value}
              title={c.label}
              onClick={() => onChange({ ...data, color: c.value })}
              className="w-7 h-7 rounded-full border-2 transition-all"
              style={{
                backgroundColor: c.value,
                borderColor: data.color === c.value ? '#fff' : 'transparent',
                boxShadow: data.color === c.value ? `0 0 0 2px ${c.value}` : 'none',
              }}
            />
          ))}
        </div>
      </Field>
    </>
  );
}

function HandoverPointForm({
  data,
  existingZones,
  onChange,
}: {
  data: Partial<HandoverPointProperties>;
  existingZones: { id: string; name: string }[];
  onChange: (d: Partial<HandoverPointProperties>) => void;
}) {
  return (
    <>
      <Field label="ID">
        <input
          className={inputClass}
          value={data.id || ''}
          placeholder="HP01"
          onChange={(e) => onChange({ ...data, id: e.target.value })}
        />
      </Field>
      <Field label="Nombre">
        <input
          className={inputClass}
          value={data.name || ''}
          placeholder="TWY S5/S6/S7 corto M"
          onChange={(e) => onChange({ ...data, name: e.target.value })}
        />
      </Field>
      <div className="mb-3">
        <label className="block text-xs font-semibold text-[var(--text-secondary)] mb-1 uppercase tracking-wide">
          Tipo de movimiento
        </label>
        <div
          className="w-full py-1.5 text-sm text-center font-bold rounded border"
          style={{
            backgroundColor: data.movement === 'ARR' ? '#22c55e20' : '#F2643020',
            borderColor: data.movement === 'ARR' ? '#22c55e' : '#F26430',
            color: data.movement === 'ARR' ? '#22c55e' : '#F26430',
          }}
        >
          {data.movement === 'ARR' ? 'LLEGADA (ARR)' : 'SALIDA (DEP)'}
        </div>
      </div>
      <ToggleGroup<RwyConfig>
        label="Configuracion de pista"
        options={[
          { label: 'SUR', value: 'SOUTH' },
          { label: 'NORTE', value: 'NORTH' },
          { label: 'ANY', value: 'ANY' },
        ]}
        value={data.rwy_config || 'ANY'}
        onChange={(v) => onChange({ ...data, rwy_config: v })}
      />
      <Field label="Pista">
        <input
          className={inputClass}
          value={data.runway || ''}
          placeholder="21L"
          onChange={(e) => onChange({ ...data, runway: e.target.value })}
        />
      </Field>
      <Field label="Zona AMS">
        <select
          className={inputClass}
          value={data.zone_id || ''}
          onChange={(e) => onChange({ ...data, zone_id: e.target.value })}
        >
          <option value="">-- Seleccionar zona --</option>
          {existingZones.map((z) => (
            <option key={z.id} value={z.id}>
              {z.id} — {z.name}
            </option>
          ))}
        </select>
      </Field>
      <Field label={`Prob. contacto: ${Math.round((data.contact_probability ?? 0.8) * 100)}%`}>
        <input
          type="range"
          min={0}
          max={1}
          step={0.05}
          value={data.contact_probability ?? 0.8}
          onChange={(e) => onChange({ ...data, contact_probability: parseFloat(e.target.value) })}
          className="w-full accent-[#F26430]"
        />
      </Field>
      <Field label="Demora contacto (s)">
        <div className="flex items-center gap-2">
          <input
            type="number"
            min={0}
            max={300}
            className={inputClass}
            value={data.contact_delay_s ?? 30}
            onChange={(e) => onChange({ ...data, contact_delay_s: parseInt(e.target.value) || 0 })}
          />
          <span className="text-xs text-[var(--text-secondary)] whitespace-nowrap">seg</span>
        </div>
      </Field>
    </>
  );
}

function PushbackProfileForm({
  data,
  topologyTaxiways,
  onChange,
}: {
  data: Partial<PushbackProfileProperties>;
  topologyTaxiways: string[];
  onChange: (d: Partial<PushbackProfileProperties>) => void;
}) {
  const [twyInput, setTwyInput] = useState(data.entry_taxiway || '');
  const [suggestions, setSuggestions] = useState<string[]>([]);

  const handleTwyChange = (val: string) => {
    setTwyInput(val);
    onChange({ ...data, entry_taxiway: val });
    if (val.length > 0) {
      setSuggestions(
        topologyTaxiways.filter((t) =>
          t.toLowerCase().startsWith(val.toLowerCase())
        ).slice(0, 6)
      );
    } else {
      setSuggestions([]);
    }
  };

  return (
    <>
      <Field label="Estacionamiento">
        <div className="px-2.5 py-1.5 rounded-md border border-[var(--border)] bg-[var(--surface-inset)] text-[var(--text-primary)] text-sm font-mono">
          {data.stand || '—'}
        </div>
      </Field>
      <Field label="Direccion">
        <input
          className={inputClass}
          value={data.direction || ''}
          placeholder="NOSE_EAST"
          onChange={(e) => onChange({ ...data, direction: e.target.value })}
        />
      </Field>
      <Field label="TWY de entrada">
        <div className="relative">
          <input
            className={inputClass}
            value={twyInput}
            placeholder="S2"
            onChange={(e) => handleTwyChange(e.target.value)}
          />
          {suggestions.length > 0 && (
            <div className="absolute z-20 w-full mt-1 rounded-md border border-[var(--border)] bg-[var(--surface)] shadow-lg">
              {suggestions.map((s) => (
                <button
                  key={s}
                  className="w-full text-left px-3 py-1.5 text-sm hover:bg-[var(--surface-raised)] text-[var(--text-primary)]"
                  onClick={() => {
                    setTwyInput(s);
                    onChange({ ...data, entry_taxiway: s });
                    setSuggestions([]);
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>
      </Field>
      <Field label="Opciones">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={data.standard ?? true}
            onChange={(e) => onChange({ ...data, standard: e.target.checked })}
            className="accent-[#F26430]"
          />
          <span className="text-sm text-[var(--text-primary)]">Retroceso estandar</span>
        </label>
      </Field>
      <Field label="ID Ruta de rodaje (opcional)">
        <input
          className={inputClass}
          value={data.taxi_route_id || ''}
          placeholder="T2-DEP-SOUTH-A"
          onChange={(e) => onChange({ ...data, taxi_route_id: e.target.value || undefined })}
        />
      </Field>
    </>
  );
}

// ─── APTR line form ───────────────────────────────────────────────────────────

const CAT_OPTIONS: { label: string; value: AircraftCat }[] = [
  { label: 'B', value: 'B' },
  { label: 'C', value: 'C' },
  { label: 'D', value: 'D' },
  { label: 'F', value: 'F' },
];

function AptrLineForm({
  data,
  featureType,
  onChange,
}: {
  data: Partial<import('../../types/conops').AptrLineProperties>;
  featureType: 'aptr_blue' | 'aptr_orange';
  onChange: (d: Partial<import('../../types/conops').AptrLineProperties>) => void;
}) {
  const color = featureType === 'aptr_blue' ? '#1E88E5' : '#F26430';
  const inputClass = 'w-full rounded-md border border-[var(--border)] bg-[var(--surface-raised)] text-[var(--text-primary)] text-sm px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#F26430]';
  return (
    <>
      <div className="mb-3 flex items-center gap-2">
        <span className="w-3 h-3 rounded-full inline-block flex-shrink-0" style={{ backgroundColor: color }} />
        <span className="text-xs font-bold uppercase tracking-wide" style={{ color }}>
          {featureType === 'aptr_blue' ? 'Eje Azul — dirección entrada' : 'Eje Naranja — dirección salida'}
        </span>
      </div>
      <Field label="Nombre / etiqueta">
        <input
          className={inputClass}
          value={data.name || ''}
          placeholder={featureType === 'aptr_blue' ? 'APTR Azul S1' : 'APTR Naranja S3'}
          onChange={(e) => onChange({ ...data, name: e.target.value })}
        />
      </Field>
      <ToggleGroup
        label="Cat. ACFT máx."
        options={CAT_OPTIONS}
        value={data.icao_cat_max || 'C'}
        onChange={(v) => onChange({ ...data, icao_cat_max: v })}
        activeColor={color}
      />
      <Field label="Notas operacionales">
        <textarea
          className={inputClass}
          rows={3}
          value={data.notes || ''}
          placeholder="Restricciones, separaciones mínimas, VMC/RAVC..."
          onChange={(e) => onChange({ ...data, notes: e.target.value || undefined })}
        />
      </Field>
    </>
  );
}

// ─── Roundabout form ──────────────────────────────────────────────────────────

function RoundaboutForm({
  data,
  onChange,
}: {
  data: Partial<import('../../types/conops').RoundaboutProperties>;
  onChange: (d: Partial<import('../../types/conops').RoundaboutProperties>) => void;
}) {
  const inputClass = 'w-full rounded-md border border-[var(--border)] bg-[var(--surface-raised)] text-[var(--text-primary)] text-sm px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#FFD700]';
  return (
    <>
      <div className="mb-3 text-xs text-[var(--text-secondary)]">
        Dibuja el perímetro del roundabout como polígono. El área interna queda marcada como zona prohibida (no-fly).
      </div>
      <Field label="Nombre">
        <input
          className={inputClass}
          value={data.name || ''}
          placeholder="Roundabout Apron 3"
          onChange={(e) => onChange({ ...data, name: e.target.value })}
        />
      </Field>
      <ToggleGroup
        label="Cat. ACFT máx."
        options={CAT_OPTIONS}
        value={data.icao_cat_max || 'C'}
        onChange={(v) => onChange({ ...data, icao_cat_max: v })}
        activeColor="#FFD700"
      />
      <Field label="Notas">
        <textarea
          className={inputClass}
          rows={3}
          value={data.notes || ''}
          placeholder="Capacidad, restricciones, referencias de diseño..."
          onChange={(e) => onChange({ ...data, notes: e.target.value || undefined })}
        />
      </Field>
    </>
  );
}

// ─── Main panel component ──────────────────────────────────────────────────────

interface ConopsFeaturePanelProps {
  mode: 'create' | 'edit' | null;
  featureType: ConopsFeatureType | null;
  initialData?: Partial<ConopsProperties>;
  existingZones: AmsZoneProperties[];
  topologyTaxiways: string[];
  onSave: (props: ConopsProperties) => void;
  onDelete?: () => void;
  onClose: () => void;
  isDark: boolean;
}

const ConopsFeaturePanel: React.FC<ConopsFeaturePanelProps> = ({
  mode,
  featureType,
  initialData,
  existingZones,
  topologyTaxiways,
  onSave,
  onDelete,
  onClose,
  isDark,
}) => {
  const [formData, setFormData] = useState<Partial<ConopsProperties>>(initialData || {});

  useEffect(() => {
    setFormData(initialData || {});
  }, [initialData, featureType]);

  if (!mode || !featureType) return null;

  const title: Record<ConopsFeatureType, string> = {
    ams_zone: 'Zona AoR',
    handover_point: 'Punto de Transferencia',
    pushback_profile: 'Perfil de Retroceso',
    taxi_route: 'Ruta de Rodaje',
    aptr_blue: 'APTR — Eje Azul',
    aptr_orange: 'APTR — Eje Naranja',
    roundabout: 'Roundabout de Plataforma',
  };

  const handleSave = () => {
    const base = { ...formData, feature_type: featureType };
    onSave(base as ConopsProperties);
  };

  const bg = isDark ? 'bg-[#1a1a1a] border-[#2a2a2a]' : 'bg-white border-gray-200';

  return (
    <div
      className={`absolute right-0 top-0 bottom-0 z-10 flex flex-col border-l shadow-xl ${bg}`}
      style={{ width: '320px' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--border)]">
        <span className="text-sm font-bold text-[var(--text-primary)] uppercase tracking-wide">
          {mode === 'create' ? 'Nuevo' : 'Editar'} — {title[featureType]}
        </span>
        <button
          onClick={onClose}
          className="p-1 rounded hover:bg-[var(--surface-raised)] text-[var(--text-secondary)]"
        >
          <X size={16} weight="bold" />
        </button>
      </div>

      {/* Form body */}
      <div className="flex-1 overflow-y-auto px-4 py-3">
        {featureType === 'ams_zone' && (
          <AmsZoneForm
            data={formData as Partial<AmsZoneProperties>}
            onChange={(d) => setFormData(d)}
          />
        )}
        {featureType === 'handover_point' && (
          <HandoverPointForm
            data={formData as Partial<HandoverPointProperties>}
            existingZones={existingZones}
            onChange={(d) => setFormData(d)}
          />
        )}
        {featureType === 'pushback_profile' && (
          <PushbackProfileForm
            data={formData as Partial<PushbackProfileProperties>}
            topologyTaxiways={topologyTaxiways}
            onChange={(d) => setFormData(d)}
          />
        )}
        {featureType === 'taxi_route' && (
          <div className="text-xs text-[var(--text-secondary)] italic">
            La ruta de rodaje se configura desde la herramienta TaxiRouteTool.
          </div>
        )}
        {(featureType === 'aptr_blue' || featureType === 'aptr_orange') && (
          <AptrLineForm
            data={formData as Partial<import('../../types/conops').AptrLineProperties>}
            featureType={featureType}
            onChange={(d) => setFormData(d)}
          />
        )}
        {featureType === 'roundabout' && (
          <RoundaboutForm
            data={formData as Partial<import('../../types/conops').RoundaboutProperties>}
            onChange={(d) => setFormData(d)}
          />
        )}
      </div>

      {/* Footer actions */}
      <div className="px-4 py-3 border-t border-[var(--border)] flex gap-2">
        {mode === 'edit' && onDelete && (
          <button
            onClick={onDelete}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-red-500 border border-red-500/30 hover:bg-red-500/10 text-sm font-medium transition-colors"
          >
            <Trash size={14} weight="bold" />
            Eliminar
          </button>
        )}
        <button
          onClick={handleSave}
          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-[#F26430] hover:bg-[#d9542a] text-white text-sm font-bold transition-colors"
        >
          <FloppyDisk size={14} weight="bold" />
          {mode === 'create' ? 'Guardar' : 'Actualizar'}
        </button>
      </div>
    </div>
  );
};

export default ConopsFeaturePanel;
